// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Constants
 * @package @geenius/analytics
 * @description Shared constants consumed by the tracker, UI variants, and
 * Storybook review surfaces. These values define provider names, polling
 * defaults, batch limits, and shared visual affordances.
 */

/** Supported analytics provider identifiers shared across all package variants. */
export const PROVIDERS = [
  "posthog",
  "mixpanel",
  "google-analytics",
  "plausible",
] as const;
/** Provider-name union derived from the shared provider registry constant. */
export type Provider = (typeof PROVIDERS)[number];

/** Default inactivity window, in milliseconds, used when rolling analytics sessions. */
export const DEFAULT_SESSION_TIMEOUT = 30 * 60 * 1000;
/** Default polling interval, in milliseconds, for realtime analytics refreshes. */
export const DEFAULT_REALTIME_INTERVAL = 5000;
/** Maximum number of analytics events to send in a single provider batch. */
export const MAX_EVENTS_PER_BATCH = 100;
/** Maximum number of steps supported when evaluating a funnel definition. */
export const MAX_FUNNEL_STEPS = 20;

/** Shared metric formatting modes consumed by the dashboard and card components. */
export const METRIC_FORMATS = {
  number: "number",
  percent: "percent",
  duration: "duration",
  currency: "currency",
} as const;

/** Shared iconography used by review surfaces and event-oriented UI components. */
export const EVENT_ICONS: Record<string, string> = {
  page_view: "📄",
  click: "👆",
  form_submit: "📝",
  custom: "⚡",
  signup: "🎉",
  purchase: "💰",
  login: "🔑",
  logout: "👋",
};

/** Semantic trend colors shared by both Tailwind and vanilla-CSS renderers. */
export const TREND_COLORS = {
  up: "var(--gn-success)",
  down: "var(--gn-danger)",
  flat: "var(--gn-text-secondary)",
} as const;
